/*
  Deferred cross-sell card images.

  The closed drawer is a fixed 100vw/100dvh panel hidden with `visibility` (never
  `display: none` — that would kill the slide-in), so as far as the preload scanner
  is concerned every card inside it is on screen and `loading="lazy"` defers
  nothing. The strip's images were downloading on every page load of the site,
  before any shopper had opened a cart.

  cart-cross-sell-card.liquid therefore parks the URLs in data-cross-sell-src /
  -srcset and this moves them onto the real attributes at the latest moment that is
  still invisible to the shopper. The card's markup and its aspect-ratio box are
  server-rendered as before, so what arrives late is pixels inside an already
  reserved slot — not layout.

  `loading` is forced to "eager" rather than simply left off: a lazy image hydrated
  inside a visibility-hidden subtree is not reliably fetched in WebKit, and WebKit
  is the engine where the drawer must not open with empty cards.
*/
window.hydrateCrossSellImages = function hydrateCrossSellImages() {
  const deferred = document.querySelectorAll('img[data-cross-sell-src]');
  if (!deferred.length) return;

  /* The same query snippet-cart-cross-sell.css uses to reveal the hover twin.
     Where it can never be seen it is never fetched, and that is most of the strip's
     weight, not half of it: the twins are lifestyle photography while the primaries
     are packshots on flat backgrounds (measured 354KB of 396KB at 480w). The markers
     are deliberately left in place on a skipped twin, so a later run on a device that
     has since gained a pointer still picks it up. */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  deferred.forEach((img) => {
    if (!canHover && img.classList.contains('cart-cross-sell-image__secondary')) return;

    const { crossSellSrc, crossSellSrcset } = img.dataset;
    img.loading = 'eager';
    if (crossSellSrcset) img.srcset = crossSellSrcset;
    img.src = crossSellSrc;

    delete img.dataset.crossSellSrc;
    delete img.dataset.crossSellSrcset;
  });
};

/*
  Warm the strip on the earliest signal the drawer is about to open, so a shopper on
  a normal connection never watches it fill in. Both triggers are safe to fire more
  than once: hydration clears its own markers.
*/
(() => {
  const hydrate = () => window.hydrateCrossSellImages();

  /* The cart icon opens the drawer without mutating the cart, so what it reveals is
     exactly the branch already in the DOM — always the right images to warm.
     #cart-icon-bubble survives Section Rendering (only its innerHTML is replaced),
     the same assumption CartDrawer.setHeaderCartIconAccessibility already makes. */
  const cartIcon = document.getElementById('cart-icon-bubble');
  if (cartIcon) {
    cartIcon.addEventListener('pointerenter', hydrate);
    cartIcon.addEventListener('focus', hydrate);
  }

  /* An add-to-cart press, anywhere on the page. Skipped while the empty-cart branch
     is on screen: that add flips the drawer to the non-empty branch, a different
     product set, so warming what is currently rendered would spend exactly the bytes
     this change saves and warm none of the images actually about to be shown. Those
     land via CartDrawer.open() the instant the re-rendered markup arrives. */
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.target.closest('button[name="add"], [data-cross-sell-open]')) return;
      if (document.querySelector('#CartDrawer .drawer__inner-empty')) return;
      hydrate();
    },
    true
  );
})();

/*
  Variant picker for the cart-drawer "Don't forget" cross-sell cards.

  Each multi-variant card embeds its variants as JSON and renders one input group per
  product option (radio chips for short lists, a select for long ones). The picker is
  parked hidden in its card; tapping the card's "+" makes the shared <cross-sell-sheet>
  adopt it into a bottom sheet. When a complete, available combination is selected this
  resolves the matching variant, writes its id into the form's hidden [name=id], and
  submits — letting the existing <product-form> handle the /cart/add request, Section
  Rendering refresh and drawer re-open.

  <product-form> guards against a double add via the submit button's aria-disabled flag,
  so a rapid second tap during the in-flight request is ignored.
*/
if (!customElements.get('cross-sell-add')) {
  customElements.define(
    'cross-sell-add',
    class CrossSellAdd extends HTMLElement {
      connectedCallback() {
        /* The document-level size-system listener is (re)bound on every connect
           and dropped on every disconnect, so it must sit OUTSIDE the one-time
           guard below: the sheet adopts and returns this node, and a listener
           bound once but removed each cycle would stop syncing after the first
           adopt. Section Rendering also replaces these cards wholesale after an
           add, and a listener left behind would leak bound to a detached node. */
        this.bindSystemSync();

        // The sheet adopts and returns this node, re-firing connectedCallback —
        // without the guard each cycle would stack another change listener.
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        this.idInput = this.form && this.form.querySelector('input[name="id"]');
        this.optionGroups = Array.from(this.querySelectorAll('[data-option-position]'));
        this.statusEl = this.querySelector('[data-cross-sell-status]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitLabel = this.submitButton && this.submitButton.querySelector('.cart-cross-sell-picker__atc-label');

        const dataEl = this.querySelector('script[type="application/json"]');
        try {
          this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
        } catch (error) {
          console.error('cross-sell-add: could not parse variant data', error);
          this.variants = [];
        }

        this.addEventListener('change', this.onOptionChange.bind(this));
        this.initSizeSystems();
      }

      /* The UK/EU/US toggle. Liquid has already rendered every system's label and
         picked the market's as active; this only swaps which one is visible, and
         it does so through the contract component-size-picker.js already owns —
         the same localStorage keys and the same document-level
         'size-picker:system' event — so the drawer and a PDP picker on the same
         page follow each other instead of disagreeing. */
      bindSystemSync() {
        if (!this.querySelector('.cart-cross-sell-option__systems')) return;
        if (!this.onSystemSync) this.onSystemSync = this.handleSystemSync.bind(this);
        document.addEventListener('size-picker:system', this.onSystemSync);
      }

      disconnectedCallback() {
        if (this.onSystemSync) document.removeEventListener('size-picker:system', this.onSystemSync);
      }

      initSizeSystems() {
        this.systemGroups = Array.from(this.querySelectorAll('.cart-cross-sell-option__systems'));
        if (!this.systemGroups.length) return;

        this.systemGroups.forEach((group) => {
          // Reveals the toggle: unstyled, it stays hidden so a blocked script
          // leaves a working grid rather than dead buttons.
          group.setAttribute('data-cross-sell-systems-ready', '');
          group.addEventListener('click', (event) => {
            const button = event.target.closest('[data-cross-sell-system-option]');
            if (!button) return;
            this.applySystem(group, button.dataset.crossSellSystemOption, true);
          });
        });

        this.systemGroups.forEach((group) => {
          const stored = this.readStored(this.storageKeyFor(group));
          if (stored) this.applySystem(group, stored, false);
        });
      }

      storageKeyFor(group) {
        const values = this.valuesFor(group);
        const system = values && values.dataset.sizeSystem;
        return system === 'cm' || system === 'in' ? 'myrqvist:size-unit' : 'myrqvist:size-system';
      }

      valuesFor(group) {
        const fieldset = group.closest('[data-option-position]');
        return fieldset && fieldset.querySelector('.cart-cross-sell-option__values');
      }

      readStored(key) {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          /* Safari private mode throws on access; a missing preference is not
             worth breaking the picker over. */
          return null;
        }
      }

      applySystem(group, system, persist) {
        if (!system) return;
        const values = this.valuesFor(group);
        if (!values || values.dataset.sizeSystem === system) return;

        // A system this option does not offer would hide every label it has.
        const offered = Array.from(group.querySelectorAll('[data-cross-sell-system-option]')).map(
          (button) => button.dataset.crossSellSystemOption
        );
        if (!offered.includes(system)) return;

        values.dataset.sizeSystem = system;
        group.querySelectorAll('[data-cross-sell-system-option]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.crossSellSystemOption === system));
        });

        if (!persist) return;
        try {
          window.localStorage.setItem(this.storageKeyFor(group), system);
        } catch (error) {
          // Preference won't persist; the picker still works this session.
        }
        document.dispatchEvent(
          new CustomEvent('size-picker:system', { detail: { system, key: this.storageKeyFor(group) } })
        );
      }

      handleSystemSync(event) {
        const { system, key } = event.detail || {};
        if (!system) return;
        this.systemGroups.forEach((group) => {
          if (this.storageKeyFor(group) !== key) return;
          this.applySystem(group, system, false);
        });
      }

      selectedValues() {
        return this.optionGroups.map((group) => {
          const checked = group.querySelector('input:checked');
          if (checked) return checked.value;
          const select = group.querySelector('select');
          return select && select.value ? select.value : undefined;
        });
      }

      onOptionChange() {
        if (!this.form || !this.idInput) return;

        const selected = this.selectedValues();
        const complete = !selected.some((value) => value === undefined);

        const match = complete
          ? this.variants.find((variant) =>
              variant.options.every((option, index) => option === selected[index])
            )
          : null;
        const valid = !!match && match.available;

        // Selecting a size only arms the explicit "Add to cart" button — no auto-add.
        this.idInput.value = valid ? match.id : '';
        if (this.submitButton) this.submitButton.disabled = !valid;
        this.updateSubmitLabel(selected);
        this.setUnavailable(complete && !valid);
        this.updateCrossGroupAvailability(selected);
      }

      // Disabled CTA names the first still-unselected option ("Select Buckle") so a
      // multi-option picker never reads as a dead-end after the first choice.
      updateSubmitLabel(selected) {
        if (!this.submitButton || !this.submitLabel) return;
        const missingIndex = selected.indexOf(undefined);
        if (missingIndex === -1) {
          if (this.submitButton.dataset.addLabel) this.submitLabel.textContent = this.submitButton.dataset.addLabel;
          return;
        }
        const legend = this.optionGroups[missingIndex].querySelector('legend');
        const template = this.submitButton.dataset.selectLabel;
        if (legend && template) this.submitLabel.textContent = template.replace('[option]', legend.textContent.trim());
      }

      // Dim values that can't complete an available variant given the OTHER groups'
      // selections (e.g. picking a belt size sells out one buckle). Inputs stay
      // enabled so the user can re-pick — Dawn's variant-picker convention.
      updateCrossGroupAvailability(selected) {
        if (this.optionGroups.length < 2) return;

        const valueAvailable = (groupIndex, value) =>
          this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[groupIndex] === value &&
              variant.options.every(
                (option, index) => index === groupIndex || selected[index] === undefined || option === selected[index]
              )
          );

        this.optionGroups.forEach((group, groupIndex) => {
          group.querySelectorAll('.cart-cross-sell-option__chip').forEach((chip) => {
            const input = chip.querySelector('input');
            if (!input || input.disabled) return;
            chip.classList.toggle('cart-cross-sell-option__chip--soldout', !valueAvailable(groupIndex, input.value));
          });

          const select = group.querySelector('select');
          if (select) {
            Array.from(select.options).forEach((optionEl) => {
              if (!optionEl.value) return;
              optionEl.disabled = !valueAvailable(groupIndex, optionEl.value);
            });
          }
        });
      }

      setUnavailable(isUnavailable) {
        this.toggleAttribute('data-unavailable', isUnavailable);
        if (this.statusEl) this.statusEl.toggleAttribute('hidden', !isUnavailable);
      }
    }
  );
}

/*
  Upgrades the curated "Don't forget" baseline into cart-aware "complete the look".

  The element ships with the curated cart_upsell list already server-rendered inside it. When
  the cart drawer opens it fetches Search & Discovery complementary recommendations for the
  cart's anchor product and replaces the baseline. An empty or failed fetch leaves the curated
  baseline untouched, so the strip is never blank and degrades without JS. The drawer re-renders
  this element on every cart change, so a fresh anchor is picked and re-fetched automatically —
  and a just-added complement drops out (the API excludes in-cart).

  The trigger is the drawer's `.active` class, not an IntersectionObserver: the closed drawer is
  an off-canvas panel, and IO false-fires on the initial layout/transform race, which would
  fetch on every page load with a non-empty cart. A cart-change re-render that happens while the
  drawer is already open loads immediately.
*/
if (!customElements.get('cart-cross-sell')) {
  customElements.define(
    'cart-cross-sell',
    class CartCrossSell extends HTMLElement {
      /*
        Every cart mutation re-renders the drawer and recreates this element, so
        without a cache each qty click would re-hit the recommendations API.
        Keyed on url + the cart's variant composition (not url alone): the API
        excludes in-cart products, so adding a recommended item must produce a
        fresh fetch that drops it from the strip, while qty-only changes reuse
        the cached strip. The key also carries the shoe/tree balance, because a
        qty change that flips it changes the shoe tree deal price on the cards.
      */
      static recsCache = new Map();

      connectedCallback() {
        if (!this.dataset.url || this.loaded) return;

        const drawer = this.closest('cart-drawer');
        if (!drawer || drawer.classList.contains('active')) {
          this.loadRecommendations();
          return;
        }

        this.openObserver = new MutationObserver(() => {
          if (!drawer.classList.contains('active')) return;
          this.openObserver.disconnect();
          this.loadRecommendations();
        });
        this.openObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      disconnectedCallback() {
        if (this.openObserver) this.openObserver.disconnect();
      }

      loadRecommendations() {
        this.loaded = true;

        const cacheKey = `${this.dataset.url}::${this.dataset.cartKey || ''}`;
        const cached = this.constructor.recsCache.get(cacheKey);
        if (cached) {
          this.swapIn(cached);
          return;
        }

        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const fetched = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.cart-drawer__cross-sell');

            if (fetched && fetched.querySelector('.cart-cross-sell-item')) {
              this.constructor.recsCache.set(cacheKey, fetched.outerHTML);
              this.swapIn(fetched.outerHTML);
            }
          })
          .catch((error) => {
            console.error('cart-cross-sell: complementary recommendations failed', error);
          });
      }

      swapIn(html) {
        this.innerHTML = html;

        /*
          The drawer's focus trap caches its first/last focusable elements when it
          opens — before this async swap replaces the strip's contents. Re-trap so
          Tab can't escape the drawer, but only when focus was lost to <body>
          (i.e. it sat on a swapped-out card), never mid-interaction elsewhere.
        */
        const drawer = this.closest('cart-drawer');
        if (
          drawer &&
          drawer.classList.contains('active') &&
          document.activeElement === document.body &&
          typeof trapFocus === 'function'
        ) {
          const container = document.getElementById('CartDrawer');
          const focusElement = drawer.querySelector('.drawer__inner');
          if (container && focusElement) trapFocus(container, focusElement);
        }
      }
    }
  );
}

/*
  Shared bottom sheet for the cross-sell variant pickers.

  One sheet per drawer (rendered in cart-drawer.liquid outside .drawer__inner so
  qty-change re-renders don't destroy it while open). Tapping a card's "+" ADOPTS
  the card's parked <cross-sell-add> into the sheet body — the form, variant JSON
  and listeners move with the node — and returns it on close. The sheet never
  clones or rebuilds picker logic.

  All triggers go through one document-level delegated click listener: elements
  are looked up at event time, so the controls keep working after every
  Section Rendering innerHTML swap without rebinding.
*/
if (!customElements.get('cross-sell-sheet')) {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cross-sell-open]');
    if (opener) {
      const drawer = opener.closest('cart-drawer');
      const sheet = drawer && drawer.querySelector('cross-sell-sheet');
      const card = opener.closest('.cart-cross-sell-item');
      if (sheet && card) sheet.openFor(card, opener);
      return;
    }

    const closer = event.target.closest('[data-sheet-close]');
    if (closer) {
      const sheet = closer.closest('cross-sell-sheet');
      if (sheet) {
        // detail === 0 -> keyboard-activated close button
        sheet.restoreToOpener = event.detail === 0;
        sheet.close();
      }
      return;
    }

    const arrow = event.target.closest('[data-scroll]');
    if (arrow) {
      const strip = arrow.closest('.cart-drawer__cross-sell');
      const list = strip && strip.querySelector('.cart-drawer__cross-sell-list');
      if (!list) return;
      const card = list.querySelector('.cart-cross-sell-item');
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const pitch = card ? card.getBoundingClientRect().width + gap : list.clientWidth;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      list.scrollBy({ left: arrow.dataset.scroll === 'prev' ? -pitch : pitch, behavior });
    }
  });

  customElements.define(
    'cross-sell-sheet',
    class CrossSellSheet extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.panel = this.querySelector('.cross-sell-sheet__panel');
        this.body = this.querySelector('[data-sheet-body]');

        // Runs before <cart-drawer>'s own Escape listener (bubbling, this is the
        // deeper node): first Escape closes the sheet, the second the drawer.
        this.addEventListener('keyup', (event) => {
          if (event.code !== 'Escape' || this.hidden) return;
          event.stopPropagation();
          this.restoreToOpener = true;
          this.close();
        });
      }

      disconnectedCallback() {
        if (this.drawerObserver) this.drawerObserver.disconnect();
      }

      openFor(card, opener) {
        const picker = card.querySelector('cross-sell-add');
        if (!picker || !this.panel || !this.body) return;

        this.card = card;
        this.opener = opener;
        this.picker = picker;

        const setText = (slot, source) => {
          const el = this.querySelector(slot);
          if (el) el.textContent = source ? source.textContent.trim() : '';
        };
        setText('[data-sheet-title]', card.querySelector('.cart-cross-sell-title'));
        setText('[data-sheet-subtitle]', card.querySelector('.cart-cross-sell-subtitle'));

        const thumb = this.querySelector('[data-sheet-thumb]');
        const image = card.querySelector('.cart-cross-sell-image__primary');
        if (thumb) thumb.replaceChildren(...(image ? [image.cloneNode(true)] : []));

        const price = this.querySelector('[data-sheet-price]');
        const cardPrice = card.querySelector('.cart-cross-sell-price');
        if (price) price.replaceChildren(...(cardPrice ? cardPrice.cloneNode(true).childNodes : []));

        this.body.append(picker);
        this.hidden = false;
        requestAnimationFrame(() => this.classList.add('is-open'));

        const drawer = this.closest('cart-drawer');
        if (drawer && !this.drawerObserver) {
          // Closing the drawer must never leave a stale-open sheet behind it.
          this.drawerObserver = new MutationObserver(() => {
            if (!drawer.classList.contains('active') && !this.hidden) this.close(true);
          });
          this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }

        // Focus the dialog itself, never the first form control: iOS
        // force-zooms a focused control under 16px (and stays zoomed) and
        // pops a focused select's picker wheel unprompted.
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
      }

      close(immediate = false) {
        if (this.hidden) return;
        this.classList.remove('is-open');

        const finish = () => {
          if (this.hidden) return;
          this.hidden = true;

          if (this.picker) {
            if (this.card && this.card.isConnected) this.card.append(this.picker);
            else this.picker.remove();
          }
          this.picker = null;
          this.card = null;

          const drawer = this.closest('cart-drawer');
          if (drawer && drawer.classList.contains('active') && typeof trapFocus === 'function') {
            const container = drawer.classList.contains('is-empty')
              ? drawer.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            // Return focus to the opening "+" only for keyboard closes —
            // after a tap, Safari treats the programmatic refocus as
            // focus-visible and paints the ring on the button.
            const focusElement =
              this.restoreToOpener && this.opener && this.opener.isConnected
                ? this.opener
                : drawer.querySelector('.drawer__inner');
            if (container && focusElement) trapFocus(container, focusElement);
          }
          this.opener = null;
          this.restoreToOpener = false;
        };

        if (immediate || !this.panel) {
          finish();
          return;
        }

        // transitionend with a duration-derived timeout fallback (reduced motion,
        // interrupted close, backgrounded tab) — same pattern as CartDrawer.close.
        const slide = getComputedStyle(this.panel).transitionDuration;
        const slideMs = parseFloat(slide) * (slide.trim().endsWith('ms') ? 1 : 1000);
        const onEnd = (event) => {
          if (event.target !== this.panel || event.propertyName !== 'transform') return;
          this.panel.removeEventListener('transitionend', onEnd);
          clearTimeout(fallback);
          finish();
        };
        const fallback = setTimeout(() => {
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        }, (Number.isFinite(slideMs) ? slideMs : 200) * 2);
        this.panel.addEventListener('transitionend', onEnd);
      }
    }
  );
}
